import React from "react";

/*
 * Help-center icons. The category glyphs are the ones drawn in the visual
 * handover's mock (2026-10-07-02-mock-help-center.html); My Account had none
 * there and uses a person outline in the same style. Stroked with currentColor
 * so the brand colour comes from CSS.
 */

const Stroke = ({ size, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CATEGORY_PATHS = {
  preOrder: (
    <>
      <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
      <path d="M3 7l9 4 9-4" />
      <path d="M12 11v10" />
    </>
  ),
  readyStock: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a4 4 0 018 0v2" />
    </>
  ),
  uniqueRequest: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
      <path d="M11 8v3l2 1" />
    </>
  ),
  orderPayments: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </>
  ),
  productInquiry: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5l3 1.5" />
    </>
  ),
  friendshipPoints: <path d="M12 20s-7-4.4-7-9a4 4 0 017-2.6A4 4 0 0119 11c0 4.6-7 9-7 9z" />,
  klaimRetur: (
    <>
      <path d="M4 4h11l5 5v11H4z" />
      <path d="M14 4v6h6" />
      <path d="M8 15h7" />
    </>
  ),
  danaRefund: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M14.5 9.5a2.8 2.8 0 00-5 1.4c0 2.4 5 1.4 5 3.6a2.8 2.8 0 01-5 1.1" />
      <path d="M12 7v10" />
    </>
  ),
  pengiriman: (
    <>
      <path d="M2 7h11v9H2z" />
      <path d="M13 10h4l3 3v3h-7z" />
      <circle cx="6.5" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </>
  ),
  alphaStore: (
    <>
      <path d="M4 21V9l8-5 8 5v12" />
      <path d="M9 21v-7h6v7" />
    </>
  ),
  legal: (
    <>
      <path d="M6 3h12v18H6z" />
      <path d="M10 6h4" />
      <path d="M9 10h6" />
      <path d="M9 14h6" />
    </>
  ),
  callService: <path d="M5 3h3l2 5-2.2 1.3a12 12 0 006 6L15 13l5 2v3a2 2 0 01-2.2 2A16.5 16.5 0 013 5.2 2 2 0 015 3z" />,
  myAccount: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </>
  )
};

export const CategoryIcon = ({ name, size = 22 }) => (
  <Stroke size={size}>{CATEGORY_PATHS[name] || CATEGORY_PATHS.productInquiry}</Stroke>
);

export const SearchIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

export const ArrowRightIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);

export const InfoIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01" />
    <path d="M11 12h1v4h1" />
  </svg>
);

export const ChatIcon = ({ size = 26 }) => (
  <Stroke size={size}>
    <path d="M21 11.5a8.4 8.4 0 01-12.1 7.5L3 21l2.1-5.7A8.4 8.4 0 1121 11.5z" />
  </Stroke>
);

export const MailIcon = ({ size = 26 }) => (
  <Stroke size={size}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </Stroke>
);

export const PhoneIcon = ({ size = 26 }) => <Stroke size={size}>{CATEGORY_PATHS.callService}</Stroke>;

/* The footer's solid WhatsApp glyph (Footer.jsx WhatsAppIcon): at 14-16px the
   outline version blurs into a blob, the silhouette stays recognisable. */
export const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.001 2C17.524 2 22.001 6.477 22.001 12C22.001 17.523 17.524 22 12.001 22C10.2338 22.003 8.49758 21.5353 6.971 20.645L2.005 22L3.357 17.032C2.46596 15.5049 1.9979 13.768 2.001 12C2.001 6.477 6.478 2 12.001 2ZM8.593 7.3L8.393 7.308C8.26369 7.31691 8.13735 7.35087 8.021 7.408C7.91257 7.46951 7.81356 7.5463 7.727 7.636C7.607 7.749 7.539 7.847 7.466 7.942C7.09612 8.4229 6.89697 9.01331 6.9 9.62C6.902 10.11 7.03 10.587 7.23 11.033C7.639 11.935 8.312 12.89 9.2 13.775C9.414 13.988 9.624 14.202 9.85 14.401C10.9534 15.3724 12.2683 16.073 13.69 16.447L14.258 16.534C14.443 16.544 14.628 16.53 14.814 16.521C15.1052 16.5056 15.3895 16.4268 15.647 16.29C15.7778 16.2223 15.9056 16.1489 16.03 16.07C16.03 16.07 16.0723 16.0413 16.155 15.98C16.29 15.88 16.373 15.809 16.485 15.692C16.569 15.6053 16.639 15.5047 16.695 15.39C16.773 15.227 16.851 14.916 16.883 14.657C16.907 14.459 16.9 14.351 16.897 14.284C16.893 14.177 16.804 14.066 16.707 14.019L16.125 13.758C16.125 13.758 15.255 13.379 14.723 13.137C14.6673 13.1128 14.6077 13.0989 14.547 13.096C14.4786 13.0888 14.4094 13.0965 14.3442 13.1184C14.279 13.1403 14.2192 13.176 14.169 13.223C14.164 13.221 14.097 13.278 13.374 14.154C13.3325 14.2098 13.2753 14.2519 13.2098 14.2751C13.1443 14.2982 13.0733 14.3013 13.006 14.284C12.9408 14.2666 12.877 14.2446 12.815 14.218C12.691 14.166 12.648 14.146 12.563 14.11C11.9889 13.8599 11.4574 13.5215 10.988 13.107C10.862 12.997 10.745 12.877 10.625 12.761C10.2316 12.3842 9.88875 11.958 9.605 11.493L9.546 11.398C9.50426 11.3338 9.47004 11.265 9.444 11.193C9.406 11.046 9.505 10.928 9.505 10.928C9.505 10.928 9.748 10.662 9.861 10.518C9.971 10.378 10.064 10.242 10.124 10.145C10.242 9.955 10.279 9.76 10.217 9.609C9.937 8.925 9.64767 8.24467 9.349 7.568C9.29 7.434 9.115 7.338 8.956 7.319C8.902 7.31233 8.848 7.307 8.794 7.303C8.65973 7.2953 8.52509 7.29663 8.391 7.307L8.593 7.3Z" />
  </svg>
);

/* Cosplay Corner's sigil, as in the footer (Footer.jsx HangerIcon). */
export const HangerIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 10V8.4a2.2 2.2 0 1 1 2.2-2.2" />
    <path d="m12 10-7.9 5.2a1.3 1.3 0 0 0 .7 2.4h14.4a1.3 1.3 0 0 0 .7-2.4L12 10Z" />
  </svg>
);
